import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Row,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import PropTypes from "prop-types";
import {
  listUnlockBoundaries,
  listUnlockBoundariesForUnlockable,
} from "../../redux/unlockBoundaries";
import { useDispatch, useSelector } from "react-redux";
import TokenPreview from "../TokenPreview";
import { REACT_APP_PROJECT_ID } from "../../utils/env";
import { getUnlockInputsAttributes } from "../../redux/unlockInputs";
import { useForm } from "react-hook-form";
import ScrollToId from "../Utils/Scroll/ScrollToId";
import ConnectWallet from "../ConnectWallet";
import { useWeb3React } from "@web3-react/core";

const UnlockTokenSelector = (props, context) => {
  const { account } = useWeb3React();
  const {
    confirmUnlockFeedbackFunction,
    isOpen,
    toggleModal,
    multiple,
    multipleMax,
    unlockableContentId,
    eventId,
    votingId,
    partnerDealId,
    unlockInputIdsArray,
  } = props;
  const [selectedTokens, setSelectedTokens] = useState([]);

  const dispatch = useDispatch();
  const onSelect = (token) => {
    if (multiple)
      setSelectedTokens((old) =>
        old.some((oldSelected) => oldSelected.identifier === token.identifier)
          ? old.filter(
              (selectedToken) => selectedToken.identifier !== token.identifier
            )
          : (multipleMax ? multipleMax > selectedTokens.length : true)
          ? [...old, token]
          : [...old]
      );
    else setSelectedTokens([token]);
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const onSubmit = (data) => {
    callConfirmFunction(data);
  };
  const unlockInputs = useSelector((state) =>
    getUnlockInputsAttributes(
      state,
      unlockInputIdsArray.map((entry) => entry.id)
    )
  );

  const hasRequiredFields = unlockInputs.find((input) => input.required);

  const callConfirmFunction = (inputValues = null) => {
    const unlockInputValues = inputValues || getValues();
    confirmUnlockFeedbackFunction(selectedTokens, unlockInputValues);
    toggleModal(false);
  };

  useEffect(() => {
    if (unlockableContentId)
      dispatch(
        listUnlockBoundaries({
          projectId: REACT_APP_PROJECT_ID,
          unlockableContentId,
        })
      );
    if (eventId)
      dispatch(
        listUnlockBoundaries({ projectId: REACT_APP_PROJECT_ID, eventId })
      );
    if (votingId)
      dispatch(
        listUnlockBoundaries({ projectId: REACT_APP_PROJECT_ID, votingId })
      );
    if (partnerDealId)
      dispatch(
        listUnlockBoundaries({ projectId: REACT_APP_PROJECT_ID, partnerDealId })
      );
  }, [dispatch, unlockableContentId, eventId, votingId, partnerDealId]);

  let unlockableObject = {};
  const unlockBoundaries = useSelector((state) => {
    if (unlockableContentId) {
      unlockableObject = {
        id: unlockableContentId,
        type: "unlockable_content",
      };
    } // TODO other cases events, votings, ...
    else if (eventId) {
      unlockableObject = {
        id: eventId,
        type: "event",
      };
    } // TODO other cases events, votings, ...
    else if (votingId) {
      unlockableObject = {
        id: votingId,
        type: "voting",
      };
    } else if (partnerDealId) {
      unlockableObject = {
        id: partnerDealId,
        type: "partner_deal",
      };
    }
    return listUnlockBoundariesForUnlockable(state, unlockableObject);
  });

  const showInputs =
    selectedTokens &&
    selectedTokens.length &&
    unlockInputs &&
    unlockInputs.length
      ? true
      : false;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => {
        toggleModal(false);
      }}
      size="xl"
      centered
      fullscreen="lg"
    >
      <ModalHeader
        toggle={() => {
          toggleModal(false);
        }}
        className="unlock-modal border-0 text-light"
      >
        {context.t("Select Token for Unlocking")}
      </ModalHeader>
      <ModalBody className="unlock-modal border-0 text-light">
        <p>
          To participate you need to select one of your tokens and add your
          details. Each token can be used once to participate.
          <br />
          <strong>
            If your token does not show up here please wait until the
            transaction is finished and in doubt refresh the page.
          </strong>
        </p>
        {unlockBoundaries.map((unlockBoundary, index) => (
          <React.Fragment key={index}>
            {unlockBoundary?.relationships?.matching_tokens?.data.map && (
              <React.Fragment key={index}>
                <Row className="list-unstyled justify-content-center mb-4">
                  {unlockBoundary?.relationships?.matching_tokens?.data.map(
                    (matchingToken, index) => (
                      <Col key={index} className="my-2" md={3} sm={6} xs={10}>
                        <TokenPreview
                          tokenId={matchingToken.id}
                          onSelect={onSelect}
                          selected={selectedTokens.some(
                            (selected) => selected.id === matchingToken.id
                          )}
                        />
                      </Col>
                    )
                  )}
                </Row>
              </React.Fragment>
            )}
          </React.Fragment>
        ))}
        {unlockBoundaries.length === 0 && (
          <div className="text-center my-5">
            <span className="h2">
              {context.t(
                "Sorry you dont have any matching Token for unlocking"
              )}
            </span>
          </div>
        )}
        {!account && (
          <div className="text-center">
            <h3>
              {context.t(
                "Please connect your wallet to be able to unlock Tokens."
              )}
            </h3>
            <ConnectWallet
              buttonColor="primary"
              buttonSize="sm"
              buttonId="nav-connect-wallet"
              className="bg-gradient border-0 text-white"
              toggleModal={toggleModal}
            />
          </div>
        )}
        {showInputs && (
          <>
            <ScrollToId id="unlock-inputs" />
            <Container
              className="d-flex justify-content-center"
              id="unlock-inputs"
            >
              <Form className="w-100 fw-bold">
                <h4 className="my-3 fw-bold">
                  {context.t(
                    "Before unlocking, please fill in the form with the required information."
                  )}
                </h4>
                <Row className="mb-3">
                  {unlockInputs.map((unlockInput, index) => (
                    <FormGroup className="my-2" key={unlockInput.id}>
                      <Col
                        xl={{ size: unlockInput.input_size }}
                        key={index}
                        className="mb-4"
                      >
                        <Label>
                          {unlockInput?.input_name}{" "}
                          {unlockInput?.required && "*"}
                        </Label>
                        <Input
                          className="border-primary unlock-modal-input"
                          type={unlockInput?.input_type}
                          invalid={errors[unlockInput?.id] ? true : false}
                          {...register(unlockInput?.id, {
                            required: unlockInput?.required
                              ? context.t("This field is required")
                              : false,
                            pattern:
                              unlockInput?.input_type === "email"
                                ? {
                                    value: /\S+@\S+\.\S+/,
                                    message: context.t(
                                      "Entered value does not match email format"
                                    ),
                                  }
                                : undefined,
                          })}
                          onChange={(evt) => {
                            setValue(unlockInput?.id, evt.target.value, {
                              shouldValidate: true,
                            });
                          }}
                        />
                      </Col>
                      {errors[unlockInput?.id] && (
                        <span role="alert" className="text-danger small mt-2">
                          {errors[unlockInput?.id].message}
                        </span>
                      )}
                    </FormGroup>
                  ))}
                </Row>
              </Form>
            </Container>
          </>
        )}
      </ModalBody>
      <ModalFooter className="unlock-modal border-0 text-light">
        <a
          className="small text-light text-decoration-none me-3"
          href="/terms"
          target="_blank"
        >
          {context.t("Terms Of Service")}
        </a>
        {unlockBoundaries.length > 0 && (
          <Button
            onClick={
              hasRequiredFields
                ? handleSubmit(onSubmit)
                : () => callConfirmFunction()
            }
            color="primary"
            disabled={
              selectedTokens.length < 1 || Object.keys(errors).length !== 0
            }
          >
            {unlockableObject.type !== "event" &&
              unlockableObject.type &&
              context.t("Unlock")}
            {unlockableObject.type === "event" && context.t("Sign Up")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

/**  define proptypes   */
UnlockTokenSelector.propTypes = {
  unlockableContentId: PropTypes.string,
  eventId: PropTypes.string,
  partnerDealId: PropTypes.string,
  confirmUnlockFeedbackFunction: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  multipleMax: PropTypes.number,
  votingId: PropTypes.string,
  unlockInputIdsArray: PropTypes.array,
};

/**  define defaultProps   */
UnlockTokenSelector.defaultProps = {
  multiple: false,
  multipleMax: null,
};

/**  define proptype for the 'translation' function  */
UnlockTokenSelector.contextTypes = {
  t: PropTypes.func,
};

export default UnlockTokenSelector;
